(() => {
  'use strict';

  const parseJson = (element, fallback) => {
    if (!element) return fallback;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.warn('[Pure Saint] JSON konnte nicht gelesen werden.', error);
      return fallback;
    }
  };

  const normalize = (value) => String(value ?? '').trim().toLocaleLowerCase('de-DE');

  const escapeCanvasText = (value) =>
    String(value ?? '')
      .replace(/[\u0000-\u001F\u007F]/g, '')
      .trim()
      .slice(0, 32);

  class PureSaint3DEngraver {
    static MATERIALS = {
      top: 'PS_Engraving_Top_Material',
      bottom: 'PS_Engraving_Bottom_Material',
      center: 'PS_Engraving_Center_Material',
      body: 'PS_Bottle_Material',
      cap: 'PS_Cap_Material',
      loop: 'PS_Loop_Material'
    };

    constructor(root, onReady) {
      this.root = root;
      this.modelViewer = root.querySelector('[data-ps-native-model] model-viewer');
      this.status = root.querySelector('[data-ps-render-status]');
      this.onReady = onReady;
      this.ready = false;
      this.failed = false;
      this.pendingConfig = null;
      this.renderTimer = null;
      this.materials = {};
      this.surfaces = {};
      this.initialize();
    }

    async initialize() {
      if (!this.modelViewer) return;

      this.root.classList.add('psp-3d-loading');
      this.setStatus('3D-Gravur wird vorbereitet', 'loading');

      const engravingModelUrl = this.root.dataset.psEngravingModelUrl;
      let sourceChanged = false;
      if (engravingModelUrl && this.modelViewer.getAttribute('src') !== engravingModelUrl) {
        // Without ios-src, model-viewer can generate Quick Look from the current scene.
        this.modelViewer.removeAttribute('ios-src');
        this.modelViewer.setAttribute('src', engravingModelUrl);
        sourceChanged = true;
      }

      try {
        await this.waitForModel(sourceChanged);

        if (!this.modelViewer.model || typeof this.modelViewer.createTexture !== 'function') {
          throw new Error('Die Materials-API des 3D-Viewers ist nicht verfügbar.');
        }

        Object.entries(PureSaint3DEngraver.MATERIALS).forEach(([key, name]) => {
          this.materials[key] = this.modelViewer.model.getMaterialByName(name);
        });

        if (!this.materials.top || !this.materials.bottom || !this.materials.body) {
          throw new Error('Das Modell enthält nicht alle Pure-Saint-Gravurmaterialien.');
        }

        const uniqueMaterials = [...new Set(Object.values(this.materials).filter(Boolean))];
        await Promise.all(uniqueMaterials.map((material) => material.ensureLoaded?.()));

        await Promise.all([
          this.createSurface('top', this.materials.top),
          this.createSurface('bottom', this.materials.bottom),
          this.createSurface('center', this.materials.center)
        ]);

        this.ready = true;
        this.root.classList.remove('psp-3d-loading', 'psp-3d-fallback');
        this.root.classList.add('psp-3d-ready');
        this.setStatus('Echte 3D-Gravur aktiv', 'ready');

        if (this.pendingConfig) this.queue(this.pendingConfig, true);
        this.onReady?.();

        this.root.dispatchEvent(new CustomEvent('pure-saint:engraving-ready', {
          bubbles: true,
          detail: { modelViewer: this.modelViewer }
        }));
      } catch (error) {
        this.failed = true;
        this.root.classList.remove('psp-3d-loading');
        this.root.classList.add('psp-3d-fallback');
        this.setStatus('Vorschau-Modus aktiv', 'fallback');
        console.warn('[Pure Saint] 3D-Gravur konnte nicht initialisiert werden.', error);
      }
    }

    waitForModel(forceNextLoad = false) {
      if (!forceNextLoad && this.modelViewer.model) return Promise.resolve();

      return new Promise((resolve, reject) => {
        const timeout = window.setTimeout(() => {
          cleanup();
          reject(new Error('Zeitüberschreitung beim Laden des 3D-Modells.'));
        }, 20000);

        const cleanup = () => {
          window.clearTimeout(timeout);
          this.modelViewer.removeEventListener('load', handleLoad);
          this.modelViewer.removeEventListener('error', handleError);
        };
        const handleLoad = () => {
          cleanup();
          resolve();
        };
        const handleError = () => {
          cleanup();
          reject(new Error('Das 3D-Modell konnte nicht geladen werden.'));
        };

        this.modelViewer.addEventListener('load', handleLoad, { once: true });
        this.modelViewer.addEventListener('error', handleError, { once: true });
      });
    }

    async createSurface(key, material) {
      if (!material) return;

      const canvas = document.createElement('canvas');
      canvas.width = 2048;
      canvas.height = 1024;
      const context = canvas.getContext('2d', { alpha: true });
      context.clearRect(0, 0, canvas.width, canvas.height);

      const texture = await this.modelViewer.createTexture(canvas);
      material.setAlphaMode?.('BLEND');
      material.setDoubleSided?.(true);
      material.pbrMetallicRoughness.setBaseColorFactor([1, 1, 1, 1]);
      material.pbrMetallicRoughness.setMetallicFactor(0.9);
      material.pbrMetallicRoughness.setRoughnessFactor(0.26);
      material.pbrMetallicRoughness.baseColorTexture?.setTexture(texture);

      this.surfaces[key] = { canvas, context, texture, material };
    }

    queue(config, immediate = false) {
      this.pendingConfig = { ...config };
      if (!this.ready) return;

      window.clearTimeout(this.renderTimer);
      this.renderTimer = window.setTimeout(() => {
        this.render(this.pendingConfig);
      }, immediate ? 0 : 72);
    }

    render(config) {
      if (!this.ready || !config) return;

      this.applyBottleColor(config);
      this.applyHandle(config);

      const activeKey = config.position === 'Unten' ? 'bottom' : 'top';
      const isEngraving = normalize(config.technique).includes('gravur');
      Object.entries(this.surfaces).forEach(([key, surface]) => {
        if (!surface) return;
        surface.context.clearRect(0, 0, surface.canvas.width, surface.canvas.height);
        if (key === activeKey) {
          surface.material.pbrMetallicRoughness.setMetallicFactor(isEngraving ? 0.9 : 0.04);
          surface.material.pbrMetallicRoughness.setRoughnessFactor(isEngraving ? 0.26 : 0.68);
          this.drawConfiguration(surface.context, config);
        }
      });
    }

    applyBottleColor(config) {
      const color = /^#[0-9a-f]{6}$/i.test(config.bottleColorHex || '')
        ? config.bottleColorHex
        : '#F4F2ED';

      [this.materials.body, this.materials.cap].forEach((material) => {
        if (!material) return;
        material.pbrMetallicRoughness.setBaseColorFactor(color);
        material.pbrMetallicRoughness.setMetallicFactor(0.1);
        material.pbrMetallicRoughness.setRoughnessFactor(0.78);
      });
    }

    applyHandle(config) {
      const material = this.materials.loop;
      if (!material) return;

      const handleValue = normalize(config.handleVariantValue || config.handle);
      const visible =
        handleValue.includes('mit tragegriff') ||
        (handleValue.startsWith('mit ') && !handleValue.includes('ohne'));

      material.setAlphaMode?.('BLEND');
      material.pbrMetallicRoughness.setBaseColorFactor(
        visible && /^#[0-9a-f]{6}$/i.test(config.bottleColorHex || '')
          ? config.bottleColorHex
          : (visible ? '#F4F2ED' : [0, 0, 0, 0])
      );
      material.pbrMetallicRoughness.setMetallicFactor(0.02);
      material.pbrMetallicRoughness.setRoughnessFactor(0.86);
    }

    drawConfiguration(context, config) {
      const text = escapeCanvasText(config.text);
      const initials = escapeCanvasText(config.initials).toUpperCase();
      const logo = this.root.querySelector('[data-ps-logo-preview]');
      const hasLogo = Boolean(config.hasLogo && logo?.complete && logo.naturalWidth > 0);
      if (!text && !initials && !hasLogo) return;

      const isEngraving = normalize(config.technique).includes('gravur');
      const canvas = context.canvas;
      const content = [];

      context.save();
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';

      if (hasLogo) {
        const ratio = Math.min(620 / logo.naturalWidth, 240 / logo.naturalHeight, 1);
        const width = logo.naturalWidth * ratio;
        const height = logo.naturalHeight * ratio;
        context.save();
        context.globalAlpha = 0.94;
        context.filter = isEngraving
          ? 'grayscale(1) contrast(1.5) brightness(1.32)'
          : 'none';
        context.drawImage(logo, (canvas.width - width) / 2, 94, width, height);
        context.restore();
      }

      if (text) content.push({ value: text, kind: 'text' });
      if (initials) content.push({ value: initials, kind: 'initials' });

      const hasUpperGraphic = hasLogo;
      const centerY = hasUpperGraphic ? 650 : 512;
      const gap = content.length > 1 ? 178 : 0;

      content.forEach((item, index) => {
        const y = centerY + (index - (content.length - 1) / 2) * gap;
        const style = this.fontStyle(config.font, item.kind);
        const fittedSize = this.fitFont(context, item.value, style, item.kind === 'initials' ? 980 : 1650);
        context.font = this.fontDeclaration(style, fittedSize);

        const tracking = item.kind === 'initials'
          ? Math.max(20, fittedSize * 0.24)
          : (style.signature ? Math.max(1, fittedSize * 0.012) : Math.max(8, fittedSize * 0.075));

        this.drawPremiumText(context, item.value, canvas.width / 2, y, tracking, {
          engraving: isEngraving,
          color: config.textColor || '#171717'
        });
      });

      context.restore();
    }

    fontStyle(font, kind) {
      if (kind === 'initials') {
        return { family: 'Arial, Helvetica, sans-serif', weight: 600, italic: false, signature: false };
      }

      const styles = {
        'Editorial Serif': { family: 'Georgia, "Times New Roman", serif', weight: 500, italic: false, signature: false },
        'Minimal Light': { family: 'Arial, Helvetica, sans-serif', weight: 300, italic: false, signature: false },
        'Signature': { family: '"Brush Script MT", "Segoe Script", cursive', weight: 400, italic: false, signature: true },
        'Modern Sans': { family: 'Arial, Helvetica, sans-serif', weight: 600, italic: false, signature: false }
      };
      return styles[font] || styles['Modern Sans'];
    }

    fontDeclaration(style, size) {
      return `${style.italic ? 'italic ' : ''}${style.weight} ${size}px ${style.family}`;
    }

    fitFont(context, value, style, maxWidth) {
      let size = style.signature ? 230 : 190;
      const min = style.signature ? 88 : 72;
      while (size > min) {
        context.font = this.fontDeclaration(style, size);
        const tracking = style.signature ? size * 0.012 : size * 0.075;
        const width = context.measureText(value).width + Math.max(0, value.length - 1) * tracking;
        if (width <= maxWidth) break;
        size -= 4;
      }
      return size;
    }

    drawPremiumText(context, value, x, y, tracking, options) {
      const text = context.font.includes('Brush Script')
        ? value
        : value.toLocaleUpperCase('de-DE');

      if (!options.engraving) {
        context.save();
        context.fillStyle = options.color;
        context.shadowColor = 'rgba(0,0,0,.14)';
        context.shadowBlur = 2;
        this.drawTrackedText(context, text, x, y, tracking, 'fill');
        context.restore();
        return;
      }

      // Dark lower edge creates the visual indentation.
      context.save();
      context.shadowColor = 'rgba(8, 10, 12, .52)';
      context.shadowBlur = 10;
      context.strokeStyle = 'rgba(43, 47, 50, .9)';
      context.lineWidth = 15;
      this.drawTrackedText(context, text, x, y + 7, tracking, 'stroke');
      context.restore();

      // Exposed-steel gradient catches the model lighting like a laser engraving.
      const steel = context.createLinearGradient(0, y - 90, 0, y + 90);
      steel.addColorStop(0, '#f8fafb');
      steel.addColorStop(0.28, '#c8cdd1');
      steel.addColorStop(0.56, '#858c91');
      steel.addColorStop(0.76, '#d9dde0');
      steel.addColorStop(1, '#a7adb1');

      context.save();
      context.fillStyle = steel;
      context.strokeStyle = 'rgba(245, 248, 249, .82)';
      context.lineWidth = 4;
      this.drawTrackedText(context, text, x, y, tracking, 'stroke');
      this.drawTrackedText(context, text, x, y, tracking, 'fill');
      context.restore();

      // A hairline shadow inside the lower edge adds a fine bevel at close zoom.
      context.save();
      context.globalAlpha = 0.48;
      context.strokeStyle = '#33383b';
      context.lineWidth = 2;
      this.drawTrackedText(context, text, x, y + 3, tracking, 'stroke');
      context.restore();
    }

    drawTrackedText(context, value, x, y, tracking, mode) {
      const glyphs = Array.from(value);
      const widths = glyphs.map((glyph) => context.measureText(glyph).width);
      const totalWidth = widths.reduce((sum, width) => sum + width, 0) + Math.max(0, glyphs.length - 1) * tracking;
      let cursor = x - totalWidth / 2;

      glyphs.forEach((glyph, index) => {
        const glyphX = cursor + widths[index] / 2;
        if (mode === 'stroke') context.strokeText(glyph, glyphX, y);
        else context.fillText(glyph, glyphX, y);
        cursor += widths[index] + tracking;
      });
    }

    setStatus(message, state) {
      if (!this.status) return;
      this.status.dataset.state = state;
      const label = this.status.querySelector('[data-ps-render-label]');
      if (label) label.textContent = message;
    }
  }

  class PureSaintPersonalizer {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-ps-form]');
      this.variants = parseJson(root.querySelector('[data-ps-variants]'), []);
      this.optionNames = parseJson(root.querySelector('[data-ps-product-options]'), []);
      this.currency = root.dataset.currency || 'EUR';
      this.previewUrl = null;
      this.lastConfig = null;

      this.elements = {
        stage: root.querySelector('[data-ps-stage]'),
        panel: root.querySelector('[data-ps-config-panel]'),
        overlay: root.querySelector('[data-ps-overlay]'),
        previewImage: root.querySelector('[data-ps-bottle-preview-image]'),
        handlePreview: root.querySelector('[data-ps-handle-preview]'),
        stageBadge: root.querySelector('[data-ps-stage-badge]'),
        previewText: root.querySelector('[data-ps-preview-text]'),
        previewInitials: root.querySelector('[data-ps-preview-initials]'),
        logoPreview: root.querySelector('[data-ps-logo-preview]'),
        tint: root.querySelector('[data-ps-bottle-tint]'),
        price: root.querySelector('[data-ps-price]'),
        comparePrice: root.querySelector('[data-ps-compare-price]'),
        variantId: root.querySelector('[data-ps-variant-id]'),
        variantMessage: root.querySelector('[data-ps-variant-message]'),
        addButton: root.querySelector('[data-ps-add]'),
        addLabel: root.querySelector('[data-ps-add-label]'),
        configJson: root.querySelector('[data-ps-config-json]'),
        configId: root.querySelector('[data-ps-config-id]'),
        textCount: root.querySelector('[data-ps-text-count]'),
        colorOutput: root.querySelector('[data-ps-color-output]'),
        printColor: root.querySelector('[data-ps-print-color]'),
        uploadFilename: root.querySelector('[data-ps-upload-filename]'),
        uploadZone: root.querySelector('[data-ps-upload-zone]')
      };

      this.engraver = new PureSaint3DEngraver(root, () => {
        if (this.lastConfig) this.updatePreview(this.lastConfig);
      });

      this.bind();
      this.update();
    }

    bind() {
      if (!this.form) return;

      this.form.addEventListener('input', () => this.update());
      this.form.addEventListener('change', (event) => {
        if (event.target.matches('[data-ps-field="logo"]')) {
          this.handleLogo(event.target.files?.[0]);
        }
        this.update();
      });

      if (this.elements.logoPreview) {
        this.elements.logoPreview.addEventListener('load', () => this.update());
      }

      this.root.querySelector('[data-ps-start]')?.addEventListener('click', () => {
        const firstField = this.form.querySelector('[data-ps-field="text"]');
        this.elements.panel?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.setTimeout(() => firstField?.focus({ preventScroll: true }), 420);
      });

      if (this.elements.uploadZone) {
        ['dragenter', 'dragover'].forEach((type) => {
          this.elements.uploadZone.addEventListener(type, () => this.elements.uploadZone.classList.add('is-dragging'));
        });
        ['dragleave', 'drop'].forEach((type) => {
          this.elements.uploadZone.addEventListener(type, () => this.elements.uploadZone.classList.remove('is-dragging'));
        });
      }

      document.addEventListener('shopify:section:load', (event) => {
        if (event.target?.contains(this.root)) this.update();
      });
    }

    getField(selector) {
      return this.form?.querySelector(`[data-ps-field="${selector}"]`);
    }

    getCheckedField(selector) {
      return this.form?.querySelector(`[data-ps-field="${selector}"]:checked`);
    }

    getConfig() {
      const bottle = this.getCheckedField('bottleColor');
      const handle = this.getCheckedField('handle');
      const technique = this.getField('technique');
      const logo = this.getField('logo');
      const text = this.getField('text')?.value.trim() || '';
      const initials = this.getField('initials')?.value.trim().toUpperCase() || '';
      const font = this.getField('font')?.value || 'Modern Sans';
      const textColor = this.getField('textColor')?.value || '#171717';
      const position = this.getField('position')?.value || 'Oben';
      const techniqueOption = technique?.selectedOptions?.[0];
      const file = logo?.files?.[0];

      return {
        text,
        initials,
        font,
        textColor,
        bottleColor: bottle?.value || '',
        bottleColorHex: bottle?.dataset.psColor || '',
        bottleVariantValue: bottle?.dataset.psVariantValue || bottle?.value || '',
        bottleImage: bottle?.dataset.psImage || '',
        bottleImageAlt: bottle?.dataset.psImageAlt || '',
        motif: '',
        position,
        handle: handle?.value || '',
        handleVariantValue: handle?.dataset.psVariantValue || handle?.value || '',
        technique: technique?.value || '',
        techniqueVariantValue: techniqueOption?.dataset.psVariantValue || technique?.value || '',
        logoFileName: file?.name || '',
        hasLogo: Boolean(file),
        timestamp: new Date().toISOString()
      };
    }

    update() {
      if (!this.form) return;
      const config = this.getConfig();
      this.lastConfig = config;
      this.updatePreview(config);
      this.updateVariant(config);
      this.updateMetadata(config);
      this.dispatch(config);
    }

    updatePreview(config) {
      const normalizedHandle = normalize(config.handleVariantValue || config.handle);
      const hasHandle =
        normalizedHandle.includes('mit tragegriff') ||
        (normalizedHandle.startsWith('mit ') && !normalizedHandle.includes('ohne'));
      const isEngraving = normalize(config.technique).includes('gravur');

      this.root.classList.toggle('psp-has-handle', hasHandle);
      this.root.classList.toggle('psp-is-engraving', isEngraving);
      this.elements.handlePreview?.classList.toggle('is-visible', hasHandle);
      if (this.elements.printColor) this.elements.printColor.hidden = isEngraving;

      if (this.elements.stageBadge) {
        const baseLabel = this.engraver.ready
          ? (isEngraving ? '360° · Live-Gravur' : '360° · Live-Druck')
          : (this.elements.stageBadge.dataset.psBaseLabel || 'Live-Vorschau');
        this.elements.stageBadge.textContent = hasHandle
          ? `${baseLabel} · Mit Tragegriff`
          : `${baseLabel} · Ohne Tragegriff`;
      }

      if (this.elements.previewImage && config.bottleImage) {
        if (this.elements.previewImage.getAttribute('src') !== config.bottleImage) {
          this.elements.previewImage.setAttribute('src', config.bottleImage);
        }
        this.elements.previewImage.setAttribute(
          'alt',
          config.bottleImageAlt || config.bottleColor || 'Pure Saint Flasche'
        );
      }

      if (this.elements.overlay) {
        this.elements.previewText.textContent = config.text;
        this.elements.previewInitials.textContent = config.initials;
        this.elements.overlay.dataset.font = config.font;
        this.elements.overlay.dataset.position =
          config.position === 'Unten' ? 'Vorne unten' : 'Vorne oben';
        this.elements.overlay.style.setProperty('--ps-preview-text-color', config.textColor);
        this.elements.overlay.classList.toggle(
          'is-empty',
          !(config.text || config.initials || config.hasLogo)
        );
      }

      this.engraver.queue(config);

      if (this.elements.tint && config.bottleColorHex) {
        this.elements.tint.style.setProperty('--ps-bottle-preview-color', config.bottleColorHex);
      }

      if (this.elements.textCount) {
        this.elements.textCount.textContent = String(config.text.length);
      }

      if (this.elements.colorOutput) {
        this.elements.colorOutput.value = config.textColor.toUpperCase();
        this.elements.colorOutput.textContent = config.textColor.toUpperCase();
      }
    }

    handleLogo(file) {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }

      if (this.elements.uploadFilename) {
        this.elements.uploadFilename.textContent = file?.name || 'Keine Datei ausgewählt';
      }

      if (!this.elements.logoPreview) return;

      if (file && file.type.startsWith('image/')) {
        this.previewUrl = URL.createObjectURL(file);
        this.elements.logoPreview.src = this.previewUrl;
        this.elements.logoPreview.hidden = false;
      } else {
        this.elements.logoPreview.removeAttribute('src');
        this.elements.logoPreview.hidden = true;
      }
    }

    updateVariant(config) {
      if (!Array.isArray(this.variants) || this.variants.length === 0) return;

      const mappings = new Map();
      const addMapping = (optionName, value) => {
        if (optionName?.trim() && value?.trim()) mappings.set(normalize(optionName), value.trim());
      };

      addMapping(this.root.dataset.handleOptionName, config.handleVariantValue);
      addMapping(this.root.dataset.colorOptionName, config.bottleVariantValue);
      addMapping(this.root.dataset.techniqueOptionName, config.techniqueVariantValue);
      addMapping(
        this.root.dataset.logoOptionName,
        config.hasLogo ? this.root.dataset.logoYesValue : this.root.dataset.logoNoValue
      );

      const activeMappings = [...mappings.entries()].filter(([name]) =>
        this.optionNames.some((optionName) => normalize(optionName) === name)
      );

      let variant = null;
      if (activeMappings.length > 0) {
        variant = this.variants.find((candidate) => activeMappings.every(([normalizedName, selectedValue]) => {
          const index = this.optionNames.findIndex((optionName) => normalize(optionName) === normalizedName);
          return index >= 0 && normalize(candidate.options?.[index]) === normalize(selectedValue);
        }));
      }

      if (!variant) {
        const currentId = Number(this.elements.variantId?.value);
        variant = this.variants.find((candidate) => Number(candidate.id) === currentId) || this.variants[0];
      }

      if (this.elements.variantId) this.elements.variantId.value = variant.id;
      if (this.elements.price) this.elements.price.textContent = this.formatMoney(variant.price);

      if (this.elements.comparePrice) {
        const showCompare = Number(variant.compare_at_price) > Number(variant.price);
        this.elements.comparePrice.hidden = !showCompare;
        this.elements.comparePrice.textContent = showCompare ? this.formatMoney(variant.compare_at_price) : '';
      }

      const exactCombinationMissing = activeMappings.length > 0 && !this.variants.some((candidate) => activeMappings.every(([normalizedName, selectedValue]) => {
        const index = this.optionNames.findIndex((optionName) => normalize(optionName) === normalizedName);
        return index >= 0 && normalize(candidate.options?.[index]) === normalize(selectedValue);
      }));

      const canAdd = Boolean(variant.available) && !exactCombinationMissing;
      const availableLabel = this.elements.addButton?.dataset.psAvailableLabel || 'In den Warenkorb';
      if (this.elements.addButton) this.elements.addButton.disabled = !canAdd;
      if (this.elements.addLabel) {
        this.elements.addLabel.textContent = exactCombinationMissing
          ? 'Kombination prüfen'
          : (variant.available ? availableLabel : 'Derzeit nicht verfügbar');
      }

      if (this.elements.variantMessage) {
        this.elements.variantMessage.hidden = !exactCombinationMissing;
        this.elements.variantMessage.textContent = exactCombinationMissing
          ? 'Für diese Auswahl ist keine exakt passende Produktvariante hinterlegt. Prüfe die Optionsnamen und Variantenwerte im Theme-Editor.'
          : '';
      }
    }

    updateMetadata(config) {
      const variantId = this.elements.variantId?.value || '';
      const compactConfig = {
        t: config.text,
        i: config.initials,
        f: config.font,
        tc: config.textColor,
        bc: config.bottleColor,
        h: config.handle,
        m: config.motif,
        p: config.position,
        v: config.technique,
        l: config.logoFileName,
        r: this.engraver.ready ? '3d-material' : 'fallback',
        vid: variantId
      };

      if (this.elements.configJson) {
        this.elements.configJson.value = JSON.stringify(compactConfig);
      }

      if (this.elements.configId && !this.elements.configId.value) {
        const randomPart = Math.random().toString(36).slice(2, 8).toUpperCase();
        this.elements.configId.value = `PS-${Date.now().toString(36).toUpperCase()}-${randomPart}`;
      }
    }

    dispatch(config) {
      const detail = {
        productId: this.root.dataset.productId,
        sectionId: this.root.dataset.sectionId,
        variantId: this.elements.variantId?.value || '',
        configuration: config,
        mount: this.root.querySelector('[data-ps-configurator-mount]'),
        modelViewer: this.engraver.modelViewer,
        materialEngravingReady: this.engraver.ready
      };

      this.root.dispatchEvent(new CustomEvent('pure-saint:configuration-change', { detail, bubbles: true }));
      window.dispatchEvent(new CustomEvent('pure-saint:configuration-change', { detail }));

      if (window.PureSaint3DAdapter && typeof window.PureSaint3DAdapter.update === 'function') {
        try {
          window.PureSaint3DAdapter.update(detail);
        } catch (error) {
          console.error('[Pure Saint] Fehler im externen 3D-Adapter.', error);
        }
      }
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
          style: 'currency',
          currency: this.currency
        }).format(amount);
      } catch (_) {
        return `${amount.toFixed(2)} ${this.currency}`;
      }
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-ps-personalizer]').forEach((root) => {
      if (root.dataset.psInitialized === 'true') return;
      root.dataset.psInitialized = 'true';
      new PureSaintPersonalizer(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
